import { useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ImageItem } from "@/types/minds-eye";
import { useVisionBoard } from "@/hooks/useVisionBoard";
import { supabase } from "@/integrations/supabase/client";
import ImageSelectionModal from "@/components/ImageSelectionModal";
import MindsEyeCycle from "@/components/MindsEyeCycle";

const GratitudePractice = () => {
  const { images: visionImages, saveVideo, uploadImages } = useVisionBoard();
  const [showSelection, setShowSelection] = useState(false);
  const [showCycle, setShowCycle] = useState(false);
  const [selectedImages, setSelectedImages] = useState<ImageItem[]>([]);

  // Convert VisionImage[] to ImageItem[] for the existing components
  const imageItems: ImageItem[] = useMemo(
    () =>
      visionImages.map((img) => ({
        id: img.id,
        url: img.url,
        name: img.name,
        type: img.type || "image",
        folder: "aspiring" as const,
        createdAt: new Date(img.createdAt),
        isAIGenerated: img.isAIGenerated,
      })),
    [visionImages]
  );

  const handleStartCycle = (selected: ImageItem[]) => {
    setSelectedImages(selected);
    setShowSelection(false);
    setShowCycle(true);
  };

  const handleCycleComplete = (imagesWithGratitude: ImageItem[]) => {
    setShowCycle(false);
  };

  const handleSaveVideoToBoard = useCallback(async (videoBlob: Blob) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const uid = sessionData.session?.user.id;
    if (!uid) throw new Error("Not authenticated");

    const ext = videoBlob.type.includes("mp4") ? "mp4" : "webm";
    const fileName = `${uid}/uploads/${crypto.randomUUID()}-gratitude-slideshow.${ext}`;
    
    const { error } = await supabase.storage
      .from("vision-board")
      .upload(fileName, videoBlob, { contentType: videoBlob.type });

    if (error) throw error;

    const { data: publicUrlData } = supabase.storage
      .from("vision-board")
      .getPublicUrl(fileName);

    await saveVideo(publicUrlData.publicUrl, "Gratitude Slideshow");
  }, [saveVideo]);

  const hasImages = visionImages.length > 0;

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className="border-b border-border px-4 py-3 flex items-center gap-3">
        <Link to="/dashboard">
          <Button variant="ghost" size="icon" className="shrink-0">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-lg font-semibold text-foreground">Gratitude Practice</h1>
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-4 text-center">
        <div className="cosmic-card p-10 max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-pink-500 to-cosmic-500 mb-6 shadow-lg">
            <Heart className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">Gratitude Practice</h2>
          <p className="text-muted-foreground text-sm mb-6">
            {hasImages
              ? "Walk through your vision board one image at a time and write a statement of gratitude - as if it's already yours."
              : "Add some images to your Vision Board first, then come back to practice gratitude with them."}
          </p>
          {hasImages ? (
            <Button className="cosmic-button" onClick={() => setShowSelection(true)}>
              Start Practice
            </Button>
          ) : (
            <Link to="/dashboard/vision-board">
              <Button className="cosmic-button">Go to Vision Board</Button>
            </Link>
          )}
        </div>
      </div>

      <ImageSelectionModal
        isOpen={showSelection}
        onClose={() => setShowSelection(false)}
        images={imageItems}
        onStartCycle={handleStartCycle}
      />

      {showCycle && (
        <MindsEyeCycle
          isOpen={showCycle}
          onClose={() => setShowCycle(false)}
          images={selectedImages}
          onComplete={handleCycleComplete}
          onCreateVideo={() => setShowCycle(false)}
          onSaveVideoToBoard={handleSaveVideoToBoard}
        />
      )}
    </div>
  );
};

export default GratitudePractice;
